// Imports
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { getContrast, setLightness, setSaturation } from 'polished';
import React, { useCallback, useEffect, useState } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import styled from 'styled-components/macro';
import MultiFieldContainer from '../../../../../../shareables/foundation/front-end/components/forms/multi-field-container';
import { visuallyHiddenLabelStyle } from '../../../../shared-components/visually-hidden-label';
import { useAppDispatch } from '../../../../../../shareables/foundation/front-end/redux/hooks';
import usePagination from '../../../../../../shareables/foundation/front-end/utils/use-pagination';
import apiActions from '../../../../../../shareables/integrations/api/redux/actions';
import theme from '../../../../../../shareables/theme';
import { GetListingsResponse } from '../../../../../../shareables/types/api/main/website/page/type/216/listings/get';
import { GeneratedThemeProps } from '../../../../../templates/template-style';
import Byline from '../shared-components/byline';
import PostLink from '../shared-components/post-link';
import UserControlButton from '../shared-components/user-control-button';
import ListingForm from './listing-form';


// Styled components
const SearchAndPageSizeWrapper = styled(MultiFieldContainer)`
	label {
		${visuallyHiddenLabelStyle}
	}
	
	margin-bottom: 0.8438rem;
`;

const ResultsHeading = styled.h3`
	display: block;
	font-size: 1.5rem;
	margin-top: 1.25rem;
	margin-bottom: 0.5313rem;
	font-weight: 500;
	line-height: 1.1;
`;

const Listing = styled.div`
	background: #fafafa;
	border: 0.0625rem solid #eee;
	border-radius: 0.1875rem;
	padding: 1rem;
	justify-content: space-between;
	align-items: center;
	
	&:not(:last-child) {
		margin-bottom: 1rem;
	}
`;

const Title = styled.h3`
	display: flex;
	gap: 0.375rem;
	margin-bottom: 0.25rem;
	line-height: 1;
	font-weight: 400;
	font-size: 1.4rem;
	justify-content: space-between;
	overflow-wrap: anywhere;
`;

const Tag = styled.span<{ theme: GeneratedThemeProps }>`
	align-items: center;
	background-color: ${(props) => props.theme.baseColor};
	border-radius: 4px;
	color: ${(props) =>
		getContrast(props.theme.baseColor, '#fff') > 1.9
			? '#fff'
			: setSaturation(0.2, setLightness(0.3, props.theme.baseColor))};
	display: inline-flex;
	font-size: 0.75rem;
	height: 2em;
	justify-content: center;
	line-height: 1.5;
	padding-left: 0.75em;
	padding-right: 0.75em;
	white-space: nowrap;
`;

const ContentContainer = styled.div`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 1.25rem;
	
	width: 100%;
	margin-top: 0.75rem;
	
	@media (max-width: ${theme.breakpoints.mobile}px) {
		flex-direction: column-reverse;
		align-items: center;
	}
`;

const ListingContent = styled.div`
	display: -webkit-box;
	overflow-y: hidden;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow-wrap: anywhere;
	max-height: 4rem;
	width: 100%;
	
	img {
		max-width: 100%;
	}
	
	p:last-child {
		margin-bottom: 0;
	}
`;

const Photo = styled.img`
	padding-left: 1rem;
	
	@media (max-width: ${theme.breakpoints.mobile}px) {
		padding-left: 0;
	}
`;


// Define the accepted props
interface Props {
	pageID: number;
	storybookListings?: GetListingsResponse;
	storybookSearchQuery?: string;
}


// Function component
const Classifieds: React.FC<Props> = ({ pageID, storybookListings, storybookSearchQuery }) => {
	// Use state
	const [allListings, setAllListings] = useState<GetListingsResponse | null>(storybookListings ?? null);
	
	
	// Use navigation
	const navigate = useNavigate();
	const isPanelOpen = useMatch('/p/:partialURI/create') !== null;
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Create search field
	const {
		items: filteredListings,
		setItems: setFilteredListings,
		searchQuery,
		searchField,
		pageSizeField,
		paginationComponent,
	} = usePagination<GetListingsResponse[0]>({
		cacheStorageKey: `${pageID ?? ''}-listings`,
		pluralDescriptor: 'listings',
		initialItems: storybookListings,
		initialSearchQuery: storybookSearchQuery,
		pageSizes: [10, 25, 50, 100],
		searchableKeys: ['title', 'text', 'submitter.formattedName', 'submitted', 'tag'],
		missingSearchableKeyFallbacks: {
			'submitter.formattedName': 'Deleted user',
		},
		pageNavigationProps: {
			addBorders: true,
		},
	});
	
	
	// Function that loads listings
	const memoizedLoadListings = useCallback(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetListingsResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/page/id_${pageID}/type/216/listings`,
				completion: (json) => {
					setAllListings(json.response);
					setFilteredListings(json.response);
				},
			})
		);
	}, [dispatch, pageID, setFilteredListings]);
	
	
	// Load listings on mount
	useEffect(() => {
		memoizedLoadListings();
	}, [memoizedLoadListings]);
	
	
	// If loading, then display nothing
	if (allListings === null || filteredListings === null) {
		return null;
	}
	
	
	// Add placeholder to search field
	const searchFieldWithPlaceholder = React.cloneElement(searchField, { placeholder: 'Search classifieds…' });
	
	
	// Return JSX
	return (
		<React.Fragment>
			<UserControlButton
				text='Add a listing'
				opened={isPanelOpen}
				onClick={() => navigate('create', { replace: true })}
				icon={faPlus}
			/>
			
			<ListingForm
				pageID={pageID}
				isPanelOpen={isPanelOpen}
				onPanelClose={() => navigate('..', { relative: 'path', replace: true })}
				onFormCompletion={memoizedLoadListings}
			/>
			
			{allListings.length === 0 && (
				<em>No listings have been posted yet. Add your listing by clicking the button above!</em>
			)}
			
			{allListings.length > 0 && (
				<SearchAndPageSizeWrapper centerOnMobile grow={1} moreSpaceBetween>
					{searchFieldWithPlaceholder}
					{pageSizeField}
				</SearchAndPageSizeWrapper>
			)}
			
			{searchQuery.length > 0 && allListings.length > 0 && (
				<React.Fragment>
					<ResultsHeading>Showing results for “{searchQuery}”</ResultsHeading>
					{filteredListings.length === 0 && <em>No results found…</em>}
				</React.Fragment>
			)}
			
			{filteredListings.map((listing) => {
				// Generate listing route
				const listingLink = `listing/${encodeURIComponent(listing.title.replace(/\W+/g, ' ').replace(/ /g, '-'))}-${listing.id}`;
				
				
				// Return JSX
				return (
					<Listing key={listing.id}>
						<Title>
							<PostLink to={listingLink} state={{ disableScrollToTop: true }} $underlineOnHover>
								{listing.title}
							</PostLink>
							
							<Tag>{listing.tag}</Tag>
						</Title>
						
						<Byline post={listing} created={listing.submitted} action='posted' expiringItemLabel='Listing' />
						
						<PostLink to={listingLink} state={{ disableScrollToTop: true }}>
							<ContentContainer>
								<ListingContent>{listing.text}</ListingContent>
								{listing.thumbnail && <Photo src={listing.thumbnail} alt='Listing photo' />}
							</ContentContainer>
						</PostLink>
					</Listing>
				);
			})}
			
			{paginationComponent}
		</React.Fragment>
	);
};

export default Classifieds;
